import { cn } from '@/lib/utils';
import type { AcademySport } from '@court-sync/shared';
import { getSportLabel, SPORT_ACCENT } from '@/lib/sportPresentation';

export interface SportFilterBadgesProps {
  sports: AcademySport[];
  value: string;
  onChange: (academySportId: string) => void;
  className?: string;
  /** Accessible label for the filter group */
  ariaLabel?: string;
}

/**
 * Horizontal sport filter pills — hidden when only one sport is active.
 */
export function SportFilterBadges({
  sports,
  value,
  onChange,
  className,
  ariaLabel = 'Filter by sport',
}: SportFilterBadgesProps) {
  if (sports.length <= 1) return null;

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => onChange('')}
        className={cn(
          'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors sm:text-sm',
          value === ''
            ? 'border-primary-600 bg-primary-50 text-primary-800 dark:border-primary-500 dark:bg-primary-950/50 dark:text-primary-200'
            : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
        )}
        aria-pressed={value === ''}
      >
        All sports
      </button>
      {sports.map((sport) => {
        const selected = value === sport.id;
        const accent = SPORT_ACCENT[sport.sportId];
        return (
          <button
            key={sport.id}
            type="button"
            onClick={() => onChange(sport.id)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors sm:text-sm',
              selected
                ? cn(accent.calendar, 'border-current shadow-sm')
                : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground'
            )}
            aria-pressed={selected}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', accent.dot)} aria-hidden />
            {getSportLabel(sport)}
          </button>
        );
      })}
    </div>
  );
}
